/**
 * Installs the global request interceptor over `window.fetch`: pre-flight token refresh, 401-then-
 * refresh, plus the network failure policy in `./network-fetch`.
 *
 * Why patch fetch instead of adding this to a client wrapper: the app reaches the API two
 * different ways. `lib/api/client.ts`'s `fetchApi` handles its own refresh, but ~28 modules under
 * `lib/api/*` call `fetch(url, { headers: getAuthHeaders() })` directly, across well over a hundred
 * call sites. Access tokens now expire in ~20 minutes rather than a day, so every one of those paths
 * needs the retry — and threading it through each by hand would be a hundred-file change where a
 * single missed call site silently becomes a spurious logout. One interception point covers them all,
 * which is the same reason HTTP libraries expose interceptors for this exact problem.
 *
 * The network policy is applied at the same seam and for the same reason: those 28 modules have no
 * shared error handling of their own (four different `!res.ok` conventions between three of them), so
 * this is the only place a dropped connection can be told apart from a broken server once, for all of
 * them.
 *
 * The 401 handling is deliberately narrow. It only acts when ALL of the following hold, so it never
 * changes the behaviour of a request it does not understand:
 *   • the response status is 401;
 *   • the request went to our own API origin;
 *   • it is not the refresh call itself (that would recurse);
 *   • the session is not an impersonation (refreshing one would silently restore the SuperAdmin);
 *   • the request body is safely replayable.
 */

import {
  getAccessToken,
  isAnonymousAuthEndpoint,
  isImpersonating,
  refreshAccessToken,
  removeAccessToken,
} from "./client";
import { fetchWithNetworkPolicy } from "./network-fetch";
import { isAccessTokenStale } from "./token-refresh-scheduler";

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:5026/api";

let installed = false;

/**
 * A body we can send twice. Strings and URLSearchParams are immutable and safe; a ReadableStream is
 * consumed by the first attempt and cannot be replayed, and FormData carrying a File is not worth the
 * risk of a partially-read upload. Anything uncertain is treated as non-replayable and simply returns
 * the original 401 — a failed retry is recoverable, a corrupted upload is not.
 */
function isReplayableBody(body: BodyInit | null | undefined): boolean {
  if (body == null) return true;
  return typeof body === "string" || body instanceof URLSearchParams;
}

/**
 * If the outgoing request carries our own Bearer token and that token is expired or within
 * the shared staleness margin of expiry, refresh proactively before dispatch. This prevents the burst
 * of 401s when a backgrounded tab wakes with a lapsed token and fires dozens of requests at once.
 * Returns the `init` to dispatch with — the caller's own when nothing was done, or a copy carrying
 * the refreshed Authorization header.
 *
 * When `input` is a Request instance, its headers are baked in and `init` is typically absent, so
 * the Authorization-header guard will normally skip it. We do not attempt to clone and rewrite
 * Request objects — a Request with a stream body cannot be safely reconstructed, and the existing
 * 401 path already declines to replay Request inputs for the same reason.
 */
async function refreshIfTokenIsStale(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
): Promise<RequestInit | undefined> {
  if (!isCarryingStaleOwnToken(input, init)) return init;

  // refreshAccessToken() is single-flight, which is the whole reason this is safe to call from
  // every request: when 152 fire at once, all 152 await the same promise and one network call
  // happens. Do not add queueing of our own on top of it.
  const refreshed = await refreshAccessToken();

  // Read the boolean rather than discarding it: refreshAccessToken reports failure by returning
  // false, not by throwing. Failure here is deliberately inert — no removeAccessToken, no
  // auth:unauthorized, no abort. The request goes out with the token we have and the existing 401
  // path below decides whether the session is genuinely dead. Pre-flight is an optimisation, so a
  // network blip during it must degrade to exactly the pre-existing reactive behaviour.
  if (!refreshed) return init;

  const freshToken = getAccessToken();
  if (!freshToken) return init;

  // Normalise through Headers because header names are case-insensitive per spec but plain objects
  // are not — the same reason the 401 retry path below does it.
  const headers = new Headers(init?.headers);
  headers.set("Authorization", `Bearer ${freshToken}`);
  return { ...init, headers };
}

/**
 * Whether this request is about to send our own access token and `isAccessTokenStale` says it is
 * expired or close enough to it to be worth renewing first.
 *
 * Pure and side-effect free, so the decision to refresh is separable from performing one. Ordered
 * cheapest-and-most-selective first: the expiry check exits for all but the last 30 seconds of a
 * token's 20-minute life, so it sits ahead of the impersonation check and a typical request costs
 * one JWT decode rather than two.
 */
function isCarryingStaleOwnToken(input: RequestInfo | URL, init: RequestInit | undefined): boolean {
  const bearerToken = extractBearerToken(init?.headers);
  if (bearerToken === null) return false;

  // Must be OUR token, not some other Bearer the caller assembled — `api-client.ts` has a
  // `lms-mock-session` fallback, and refreshing or rewriting on behalf of that is not ours to do.
  const currentToken = getAccessToken();
  if (!currentToken || bearerToken !== currentToken) return false;

  const url =
    typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;
  if (!API_BASE_URL || !url.startsWith(API_BASE_URL)) return false;

  // Excluding the anonymous endpoints is also what stops refreshAccessToken()'s own fetch to
  // /Auth/refresh from recursing back into this check.
  if (isAnonymousAuthEndpoint(url)) return false;

  if (!isAccessTokenStale(currentToken, Date.now())) return false;

  // The refresh cookie belongs to the SuperAdmin, so renewing an impersonation session would swap
  // "viewing as" for a real admin one behind the user's back.
  return !isImpersonating();
}

/**
 * Reads the Bearer token from a `HeadersInit`, returning `null` when there is no Authorization
 * header or it is not a Bearer scheme.
 *
 * `HeadersInit` is a union of three shapes (a `Headers`, a `[name, value][]`, or a plain object) and
 * header names are case-insensitive, so hand-rolling this means three branches and a casing policy.
 * The `Headers` constructor already normalises all three and `get` is case-insensitive by spec —
 * this is the same normalisation the 401 retry path below relies on.
 *
 * The constructor throws `TypeError` on a syntactically invalid header name or value. That request
 * was going to fail in `fetch` for the same reason anyway, so we treat it as "no token" and let it
 * proceed — a malformed header must not become an exception thrown out of a patched `window.fetch`.
 * Anything other than `TypeError` is unexpected and propagates.
 */
function extractBearerToken(headers: HeadersInit | undefined): string | null {
  if (!headers) return null;
  try {
    const auth = new Headers(headers).get("Authorization");
    return auth?.startsWith("Bearer ") ? auth.slice(7) : null;
  } catch (error) {
    if (error instanceof TypeError) return null;
    throw error;
  }
}

export function installAuthInterceptor(): void {
  // Idempotent: React Strict Mode double-invokes effects in development, and wrapping the wrapper
  // would stack a second retry on every request.
  if (installed || typeof window === "undefined") return;
  installed = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    // ── Pre-flight: refresh a stale token BEFORE dispatch ─────────────────────
    // This is what eliminates the burst of 401s from a backgrounded tab. See refreshIfTokenIsStale
    // for the full guard chain and rationale.
    const effectiveInit = await refreshIfTokenIsStale(input, init);

    // Deliberately not wrapped in try/catch here: a network failure has already been classified into a
    // user-facing message by the policy, and there is no 401 to inspect on a request that never
    // reached the server.
    const response = await fetchWithNetworkPolicy(originalFetch, input, effectiveInit);

    if (response.status !== 401) return response;

    const url =
      typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;

    // Only our own API, and never the refresh endpoint (which answers 401 when the refresh token
    // itself is dead — retrying that is an infinite loop).
    if (!API_BASE_URL || !url.startsWith(API_BASE_URL)) return response;
    if (url.includes("/Auth/refresh")) return response;

    // An impersonation session must fail closed here. The refresh cookie belongs to the SuperAdmin,
    // so renewing it would hand back a real admin session in place of the "viewing as" one.
    if (isImpersonating()) return response;

    // A Request object's body is an already-consumed stream by this point; only the (url, init) form
    // can be replayed, and only for body types that survive a second read.
    if (input instanceof Request || !isReplayableBody(effectiveInit?.body)) return response;

    const refreshed = await refreshAccessToken();
    if (!refreshed) {
      // Both 401 paths must agree on cleanup. fetchApi (the other route into the API) already
      // clears tokens and fires this event; without it here, the ~28 raw-fetch modules and the
      // polling loops keep hammering with a dead token forever.
      removeAccessToken();
      window.dispatchEvent(new Event("auth:unauthorized"));
      return response;
    }

    // Replace the stale bearer token, preserving every other header the caller set. Header names are
    // case-insensitive per the spec but plain objects are not, so normalise via Headers rather than
    // deleting a guessed casing of "Authorization".
    const headers = new Headers(effectiveInit?.headers);
    const token = getAccessToken();
    if (token) headers.set("Authorization", `Bearer ${token}`);

    // Policy applied per attempt, so the replay gets its own full deadline rather than inheriting
    // whatever was left of the first attempt's.
    return fetchWithNetworkPolicy(originalFetch, url, { ...effectiveInit, headers });
  };
}
